import * as React from 'react'
import { ChevronDown, X, Search, PlusCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'

export interface MultiSelectOption {
  value: string
  label: string
  icon?: React.ReactNode
}

type DisplayMode = 'badges' | 'compact'

interface MultiSelectProps {
  options: MultiSelectOption[]
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  disabled?: boolean
  className?: string
  placeholderClassName?: string
  showIconsInTrigger?: boolean
  displayMode?: DisplayMode
  searchable?: boolean
  searchPlaceholder?: string
  showCompactIcon?: boolean
  showCompactOverflowCount?: boolean
  id?: string
  'aria-label'?: string
}

export function MultiSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  className,
  placeholderClassName,
  showIconsInTrigger = false,
  displayMode = 'badges',
  searchable = true,
  searchPlaceholder = 'Search...',
  showCompactIcon = true,
  showCompactOverflowCount = false,
  id,
  'aria-label': ariaLabel,
}: MultiSelectProps) {
  const [search, setSearch] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [visibleCount, setVisibleCount] = React.useState<number | null>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const measureRef = React.useRef<HTMLDivElement>(null)

  const filteredOptions = React.useMemo(() => {
    if (!search.trim()) return options
    const searchLower = search.toLowerCase()
    return options.filter(option =>
      option.label.toLowerCase().includes(searchLower)
    )
  }, [options, search])

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen)
    if (!isOpen) setSearch('')
  }

  const handleSelect = (optionValue: string) => {
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue))
    } else {
      onChange([...value, optionValue])
    }
  }

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation()
    onChange(value.filter(v => v !== optionValue))
  }

  const selectedOptions = value
    .map(v => options.find(o => o.value === v))
    .filter((opt): opt is MultiSelectOption => !!opt)

  const measuresCompactOverflow = displayMode === 'compact' && showCompactOverflowCount

  const calculateVisibleCount = React.useCallback(() => {
    if ((displayMode !== 'badges' && !measuresCompactOverflow) || selectedOptions.length === 0) {
      setVisibleCount(null)
      return
    }

    const container = containerRef.current
    const measureContainer = measureRef.current
    if (!container || !measureContainer) return

    const containerWidth = container.offsetWidth
    const badges = measureContainer.children
    const gap = measuresCompactOverflow ? 0 : 4
    const ellipsisWidth = measuresCompactOverflow ? 32 : 40

    let totalWidth = 0
    let fitCount = 0

    for (let i = 0; i < badges.length; i++) {
      const badge = badges[i] as HTMLElement
      const badgeWidth = badge.offsetWidth + (i > 0 ? gap : 0)

      const needsEllipsis = i < badges.length - 1
      const reservedSpace = needsEllipsis ? ellipsisWidth + gap : 0

      if (totalWidth + badgeWidth + reservedSpace <= containerWidth) {
        totalWidth += badgeWidth
        fitCount++
      } else {
        break
      }
    }

    if (fitCount === badges.length) {
      setVisibleCount(null)
    } else {
      setVisibleCount(Math.max(1, fitCount))
    }
  }, [displayMode, measuresCompactOverflow, selectedOptions.length])

  React.useLayoutEffect(() => {
    calculateVisibleCount()
  }, [calculateVisibleCount, selectedOptions])

  React.useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleCount()
    })

    resizeObserver.observe(container)
    return () => resizeObserver.disconnect()
  }, [calculateVisibleCount])

  const getCompactIcon = () => {
    if (!showCompactIcon || selectedOptions.length === 0) return null
    if (selectedOptions.length === 1) {
      return selectedOptions[0].icon
    }
    return <PlusCircle className="h-4 w-4" />
  }

  const getCompactLabel = () => {
    if (selectedOptions.length === 0) return null
    return selectedOptions.map(o => o.label).join(', ')
  }

  const hiddenCompactCount = visibleCount !== null ? selectedOptions.length - visibleCount : 0
  const visibleCompactLabel = visibleCount !== null
    ? selectedOptions.slice(0, visibleCount).map(o => o.label).join(', ')
    : getCompactLabel()

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          id={id}
          aria-label={ariaLabel && `${ariaLabel}: ${getCompactLabel() ?? placeholder}`}
          className={cn(
            'relative flex h-10 w-full items-center justify-between rounded-md border border-border bg-input-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <div ref={containerRef} className="flex gap-1 flex-1 items-center min-w-0 overflow-hidden">
            {displayMode === 'badges' && selectedOptions.length > 0 && (
              <div
                ref={measureRef}
                className="flex gap-1 items-center absolute invisible pointer-events-none"
                aria-hidden="true"
              >
                {selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="shrink-0 flex items-center gap-1"
                  >
                    {showIconsInTrigger && option.icon && (
                      <span className="shrink-0">{option.icon}</span>
                    )}
                    {option.label}
                    <span className="ml-1 h-3 w-3" />
                  </Badge>
                ))}
              </div>
            )}
            {measuresCompactOverflow && selectedOptions.length > 0 && (
              <div
                ref={measureRef}
                className="flex items-center absolute invisible pointer-events-none whitespace-pre"
                aria-hidden="true"
              >
                {selectedOptions.map((option, i) => (
                  // Pseudo-element text is laid out (so it measures) but stays out of the trigger's text content.
                  <span key={option.value} data-label={i > 0 ? `, ${option.label}` : option.label} className="before:content-[attr(data-label)]" />
                ))}
              </div>
            )}
            {selectedOptions.length > 0 ? (
              displayMode === 'compact' ? (
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {getCompactIcon() && (
                    <span className="shrink-0">{getCompactIcon()}</span>
                  )}
                  <span className="truncate">{visibleCompactLabel}</span>
                  {hiddenCompactCount > 0 && (
                    <span className="shrink-0 text-muted-foreground">+{hiddenCompactCount}</span>
                  )}
                </div>
              ) : (
                <div className="flex gap-1 items-center">
                  {(visibleCount !== null ? selectedOptions.slice(0, visibleCount) : selectedOptions).map((option) => (
                    <Badge
                      key={option.value}
                      variant="secondary"
                      className="shrink-0 flex items-center gap-1"
                    >
                      {showIconsInTrigger && option.icon && (
                        <span className="shrink-0">{option.icon}</span>
                      )}
                      {option.label}
                      <button
                        type="button"
                        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onPointerDown={(e) => e.preventDefault()}
                        onClick={(e) => handleRemove(option.value, e)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  {visibleCount !== null && selectedOptions.length > visibleCount && (
                    <Badge variant="secondary" className="shrink-0">
                      +{selectedOptions.length - visibleCount}
                    </Badge>
                  )}
                </div>
              )
            ) : (
              <span className={cn('text-muted-foreground', placeholderClassName)}>{placeholder}</span>
            )}
          </div>
          <ChevronDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]" align="start">
        {searchable && (
          <div className="p-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8 h-8"
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
        <div className={cn(searchable && 'max-h-60 overflow-y-auto')}>
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <DropdownMenuCheckboxItem
                key={option.value}
                checked={value.includes(option.value)}
                onCheckedChange={() => handleSelect(option.value)}
                onSelect={(e) => e.preventDefault()}
              >
                {option.icon && <span className="shrink-0">{option.icon}</span>}
                {option.label}
              </DropdownMenuCheckboxItem>
            ))
          ) : (
            <div className="px-2 py-4 text-center text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
